import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router'
import { useSettings } from '@/lib/queries'
import { plainText } from '@/lib/utils'
import { SiteHeader } from './site-header'
import { SiteFooter } from './site-footer'
import { ErrorBoundary } from '@/components/ui/error-boundary'

/** Scrolls to the top on navigation, but leaves in-page hash links alone. */
function ScrollToTop() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) return
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [pathname, hash])
  return null
}

/**
 * Keeps the document title and meta description in step with the CMS. The site
 * is a SPA, so this is what a browser tab and a pasted link actually show.
 */
export function usePageMeta(title?: string, description?: string) {
  const { data: settings } = useSettings()
  const clubName = settings?.club_name ?? 'AFC Vikings Cairo'
  const fallbackDescription = settings?.seo_description

  useEffect(() => {
    document.title = title
      ? `${title} — ${clubName}`
      : [clubName, settings?.tagline].filter(Boolean).join(' — ')

    const content = description ?? fallbackDescription
    if (!content) return
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', plainText(content, 300))
  }, [title, description, clubName, settings?.tagline, fallbackDescription])
}

export function SiteLayout() {
  const { pathname } = useLocation()

  return (
    <div className="grain relative flex min-h-dvh flex-col">
      <ScrollToTop />
      <SiteHeader />
      {/* No top padding here: every page opens with its own hero, which sets
          the clearance it needs under the fixed header. */}
      <main id="main" className="flex-1">
        {/* Keyed on the route so navigating away clears a caught error. */}
        <ErrorBoundary key={pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
      <SiteFooter />
    </div>
  )
}
